/**
 * PageGuide — Educational helper / "How-to-use" collapsible card
 *
 * Renders an expandable tip card at the top of a page that teaches users
 * what a feature does, its key concepts, and step-by-step usage examples.
 * The card can be dismissed (collapsed) and the preference is stored in
 * localStorage so it only shows once unless reopened.
 *
 * [T66P0] Educational helper guides for all pages
 * [T81P1-d] Redesigned with tabbed UI — Overview, Options, Examples, Tips
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Lightbulb,
  ChevronDown,
  ChevronUp,
  X,
  BookOpen,
  ArrowRight,
  Info,
  AlertTriangle,
  AlertOctagon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GuideStep {
  title: string;
  description: string;
}

export interface GuideTip {
  emoji?: string;
  text: string;
}

export interface GuideOption {
  name: string;
  type?: string;
  required?: boolean;
  description: string;
  consequence: string;
  color?: 'green' | 'red' | 'blue' | 'amber' | 'purple';
}

export interface GuideWarning {
  text: string;
  severity: 'info' | 'caution' | 'destructive';
}

export interface GuideExample {
  title: string;
  scenario: string;
}

export interface PageGuideContent {
  /** Page title displayed in the guide header */
  pageTitle: string;
  /** Short intro paragraph explaining the page */
  intro: string;
  /** Key concepts / vocabulary defined on this page */
  concepts?: { term: string; definition: string }[];
  /** Step-by-step walkthrough */
  steps?: GuideStep[];
  /** Pro tips */
  tips?: GuideTip[];
  /** Field/option reference with consequence descriptions */
  options?: GuideOption[];
  /** Warnings about irreversible or high-impact actions */
  warnings?: GuideWarning[];
  /** Concrete usage examples */
  examples?: GuideExample[];
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'fintrack:guide-dismissed:';

function isDismissed(pageKey: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${pageKey}`) === '1';
  } catch {
    return false;
  }
}

function setDismissed(pageKey: string, val: boolean) {
  try {
    if (val) {
      localStorage.setItem(`${STORAGE_PREFIX}${pageKey}`, '1');
    } else {
      localStorage.removeItem(`${STORAGE_PREFIX}${pageKey}`);
    }
  } catch {
    /* quota */
  }
}

// ---------------------------------------------------------------------------
// Border-left color map (full Tailwind class names — no dynamic interpolation)
// ---------------------------------------------------------------------------

const optionBorderClass: Record<NonNullable<GuideOption['color']>, string> = {
  green: 'border-l-green-600',
  red: 'border-l-red-600',
  blue: 'border-l-blue-500',
  amber: 'border-l-amber-500',
  purple: 'border-l-purple-500',
};

const warningStyles: Record<
  GuideWarning['severity'],
  { borderClass: string; bgClass: string; Icon: React.ElementType; iconClass: string }
> = {
  info: {
    borderClass: 'border-l-blue-500',
    bgClass: 'bg-blue-50 dark:bg-blue-950/30',
    Icon: Info,
    iconClass: 'text-blue-500',
  },
  caution: {
    borderClass: 'border-l-amber-500',
    bgClass: 'bg-amber-50 dark:bg-amber-950/30',
    Icon: AlertTriangle,
    iconClass: 'text-amber-500',
  },
  destructive: {
    borderClass: 'border-l-red-600',
    bgClass: 'bg-red-50 dark:bg-red-950/30',
    Icon: AlertOctagon,
    iconClass: 'text-red-600',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PageGuideProps {
  /** Unique page identifier for persisting dismiss state */
  pageKey: string;
  content: PageGuideContent;
}

export default function PageGuide({ pageKey, content }: PageGuideProps) {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissedState] = useState(true); // default hidden to prevent flash

  useEffect(() => {
    setDismissedState(isDismissed(pageKey));
  }, [pageKey]);

  const handleDismiss = useCallback(() => {
    setDismissedState(true);
    setExpanded(false);
    setDismissed(pageKey, true);
  }, [pageKey]);

  const handleReopen = useCallback(() => {
    setDismissedState(false);
    setExpanded(true);
    setDismissed(pageKey, false);
  }, [pageKey]);

  const hasOptions = (content.options?.length ?? 0) > 0;
  const hasExamples = (content.examples?.length ?? 0) > 0;

  // Collapsed trigger bar (always visible when dismissed)
  if (dismissed) {
    return (
      <button
        onClick={handleReopen}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
        aria-label={`Show ${content.pageTitle} guide`}
      >
        <BookOpen className="h-3.5 w-3.5" />
        <span>How to use {content.pageTitle}</span>
      </button>
    );
  }

  return (
    <Card className="border-primary/20 bg-primary/5 transition-all duration-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-full">
              <Lightbulb className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-base">
              How to use {content.pageTitle}
            </CardTitle>
            <Badge variant="secondary" className="text-[10px]">Guide</Badge>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse guide' : 'Expand guide'}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleDismiss}
              aria-label="Dismiss guide"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Intro always visible when card is open */}
        <p className="text-sm text-muted-foreground leading-relaxed">{content.intro}</p>

        {expanded && (
          <div className="animate-in fade-in-50 slide-in-from-top-2 duration-200">
            <Tabs defaultValue="overview">
              {/* Tab bar */}
              <TabsList className="h-8 w-full justify-start bg-transparent border-b border-border rounded-none px-0 mb-4 overflow-x-auto scrollbar-none">
                <TabsTrigger
                  value="overview"
                  className="h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground px-3 text-xs font-medium transition-colors hover:text-foreground"
                >
                  Overview
                </TabsTrigger>
                {hasOptions && (
                  <TabsTrigger
                    value="options"
                    className="h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground px-3 text-xs font-medium transition-colors hover:text-foreground"
                  >
                    Options
                  </TabsTrigger>
                )}
                {hasExamples && (
                  <TabsTrigger
                    value="examples"
                    className="h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground px-3 text-xs font-medium transition-colors hover:text-foreground"
                  >
                    Examples
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="tips"
                  className="h-8 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground px-3 text-xs font-medium transition-colors hover:text-foreground"
                >
                  Tips
                </TabsTrigger>
              </TabsList>

              {/* ---- Tab 1: Overview ---- */}
              <TabsContent value="overview" className="space-y-5 mt-0">
                {/* Key Concepts grid */}
                {content.concepts && content.concepts.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Key Concepts</h4>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {content.concepts.map((c) => (
                        <div key={c.term} className="rounded-md border bg-card p-2.5">
                          <span className="text-xs font-semibold text-primary">{c.term}</span>
                          <p className="text-xs text-muted-foreground mt-0.5">{c.definition}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Getting Started steps */}
                {content.steps && content.steps.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Getting Started</h4>
                    <ol className="space-y-2">
                      {content.steps.map((step, i) => (
                        <li key={i} className="flex gap-3">
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                            {i + 1}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{step.title}</p>
                            <p className="text-xs text-muted-foreground">{step.description}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </TabsContent>

              {/* ---- Tab 2: Options ---- */}
              {hasOptions && (
                <TabsContent value="options" className="mt-0">
                  {/* Desktop rows */}
                  <div className="hidden sm:block space-y-1">
                    {content.options!.map((opt, i) => (
                      <div
                        key={i}
                        className={cn(
                          'flex items-start gap-3 rounded-md border border-l-4 bg-card p-3',
                          opt.color ? optionBorderClass[opt.color] : 'border-l-border'
                        )}
                      >
                        {/* Name + type badge */}
                        <div className="w-40 shrink-0">
                          <span className="text-xs font-semibold text-foreground">
                            {opt.name}
                          </span>
                          {opt.type && (
                            <Badge variant="outline" className="ml-1.5 text-[10px] px-1.5 py-0">
                              {opt.type}
                            </Badge>
                          )}
                        </div>
                        {/* Description */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">{opt.description}</p>
                        </div>
                        {/* Consequence */}
                        <div className="w-56 shrink-0">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">→ </span>
                            {opt.consequence}
                          </p>
                        </div>
                        {/* Required badge */}
                        <div className="w-20 shrink-0 text-right">
                          <Badge
                            variant={opt.required ? 'default' : 'secondary'}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {opt.required ? 'Required' : 'Optional'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile stacked cards */}
                  <div className="sm:hidden space-y-2">
                    {content.options!.map((opt, i) => (
                      <div
                        key={i}
                        className={cn(
                          'rounded-md border border-l-4 bg-card p-3 space-y-1',
                          opt.color ? optionBorderClass[opt.color] : 'border-l-border'
                        )}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-foreground">{opt.name}</span>
                          {opt.type && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {opt.type}
                            </Badge>
                          )}
                          <Badge
                            variant={opt.required ? 'default' : 'secondary'}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {opt.required ? 'Required' : 'Optional'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{opt.description}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">→ </span>
                          {opt.consequence}
                        </p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}

              {/* ---- Tab 3: Examples ---- */}
              {hasExamples && (
                <TabsContent value="examples" className="mt-0 space-y-3">
                  {content.examples!.map((ex, i) => (
                    <div key={i} className="rounded-md border overflow-hidden">
                      <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 border-b">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                          {i + 1}
                        </span>
                        <span className="text-xs font-semibold">{ex.title}</span>
                      </div>
                      <p className="px-3 py-2.5 text-xs text-muted-foreground leading-relaxed">
                        {ex.scenario}
                      </p>
                    </div>
                  ))}
                </TabsContent>
              )}

              {/* ---- Tab 4: Tips ---- */}
              <TabsContent value="tips" className="mt-0 space-y-3">
                {/* Warnings */}
                {content.warnings && content.warnings.length > 0 && (
                  <div className="space-y-2">
                    {content.warnings.map((w, i) => {
                      const style = warningStyles[w.severity];
                      const WarnIcon = style.Icon;
                      return (
                        <div
                          key={i}
                          className={cn(
                            'flex items-start gap-2 rounded-md border border-l-4 p-3',
                            style.borderClass,
                            style.bgClass
                          )}
                        >
                          <WarnIcon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', style.iconClass)} />
                          <p className="text-xs text-foreground leading-relaxed">{w.text}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Separator between warnings and tips */}
                {content.warnings && content.warnings.length > 0 && content.tips && content.tips.length > 0 && (
                  <Separator className="my-1" />
                )}

                {/* Pro tips */}
                {content.tips && content.tips.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">💡 Pro Tips</h4>
                    <ul className="space-y-1.5">
                      {content.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <ArrowRight className="h-3 w-3 mt-0.5 shrink-0 text-primary" />
                          <span>{tip.emoji ? `${tip.emoji} ` : ''}{tip.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Edge case: no warnings and no tips */}
                {(!content.warnings || content.warnings.length === 0) &&
                  (!content.tips || content.tips.length === 0) && (
                    <p className="text-xs text-muted-foreground">No tips available for this page yet.</p>
                  )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
